import { useEffect, useState } from "react";
import { Building2, Check, Handshake, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { cn } from "@/lib/cn";

export type OrigemDoAceite = { tipo: "convite"; id: string } | { tipo: "token"; token: string };

type Props = {
  aberto: boolean;
  origem: OrigemDoAceite | null;
  /** `descartar` distingue fechar de desistir: quem fecha sem ter nenhuma clínica pra aceitar não
   *  está recusando o convite, só não pode agir agora — e quem abriu por link precisa guardar o
   *  token, que não tem outra porta de entrada. */
  onFechar: (descartar: boolean) => void;
  /** Chamado depois de conectar (ou dispensar) — quem abre limpa o próprio estado aqui. */
  onSucesso: () => void;
};

/**
 * Escolher EM QUAIS clínicas aceitar um convite de parceria do laboratório.
 *
 * Um dentista com várias clínicas conecta todas de uma vez (vêm pré-marcadas) ou escolhe —
 * o aceite deixou de depender da organização ativa. Só administrador/dono de cada clínica pode
 * aceitar (regra de papel, não de permissão configurável). Clínica já conectada aparece marcada
 * "Parceria já existe"; onde o papel não permite, a linha explica em vez de sumir.
 */
export function AceiteMultiClinicaDialog({ aberto, origem, onFechar, onSucesso }: Props) {
  const utils = trpc.useUtils();
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  const consulta = trpc.partner.connectInvite.clinicasParaAceite.useQuery(
    origem?.tipo === "convite" ? { id: origem.id } : { token: origem?.token ?? "" },
    { enabled: aberto && !!origem, retry: false },
  );
  const dados = consulta.data;
  const clinicas = dados?.clinicas ?? [];
  // O laboratório decidiu na geração: multiClinic libera a seleção múltipla; sem ela, o convite
  // conecta UMA clínica e a lista vira escolha única.
  const multiClinic = dados?.multiClinic ?? false;
  const elegiveis = clinicas.filter((c) => c.podeAceitar && !c.jaConectada);
  const todasConectadas = clinicas.length > 0 && clinicas.every((c) => c.jaConectada);

  // Multi: pré-marca todas as elegíveis ("aceitar para todas" em um clique; desmarcar é a
  // exceção). Única: pré-marca só a primeira elegível.
  useEffect(() => {
    if (!dados) return;
    setSelecionadas(
      multiClinic
        ? new Set(elegiveis.map((c) => c.organizationId))
        : new Set(elegiveis.slice(0, 1).map((c) => c.organizationId)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  const invalidar = () => {
    utils.prosthesis.lab.list.invalidate();
    utils.prosthesis.lab.options.invalidate();
    utils.partner.connectInvite.listMine.invalidate();
    utils.notification.invalidate();
  };

  const aoConectar = (r: { conectadas: number }) => {
    toast.success(
      r.conectadas === 1 ? "Parceria conectada em 1 clínica." : `Parceria conectada em ${r.conectadas} clínicas.`,
    );
    invalidar();
    onSucesso();
  };
  const aoFalhar = (e: { message?: string }) => toast.error(e.message || "Erro ao aceitar o convite.");

  const aceitarConvite = trpc.partner.connectInvite.acceptMine.useMutation({ onSuccess: aoConectar, onError: aoFalhar });
  const aceitarToken = trpc.partner.connectInvite.accept.useMutation({
    onSuccess: (r) => ("conectadas" in r ? aoConectar(r) : undefined),
    onError: aoFalhar,
  });
  const dispensar = trpc.partner.connectInvite.declineMine.useMutation({
    onSuccess: () => {
      toast.success("Convite dispensado.");
      invalidar();
      onSucesso();
    },
    onError: aoFalhar,
  });
  const enviando = aceitarConvite.isPending || aceitarToken.isPending || dispensar.isPending;

  // Sem clínica na conta não há o que aceitar: fechar aqui preserva o convite pra depois de a
  // pessoa criar (ou converter) uma clínica; descartar só pelo botão explícito.
  const semClinicas = !consulta.isLoading && !consulta.isError && clinicas.length === 0;
  const fechar = () => onFechar(!semClinicas);

  const alternar = (id: string) =>
    setSelecionadas((prev) => {
      if (!multiClinic) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const aceitar = () => {
    if (!origem || selecionadas.size === 0) return;
    const organizationIds = [...selecionadas];
    if (origem.tipo === "convite") aceitarConvite.mutate({ id: origem.id, organizationIds });
    else aceitarToken.mutate({ token: origem.token, organizationIds });
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-foreground" ajuda={AJUDA_EQUIPE_E_PARCEIROS.aceiteDeParceria}>
            <Handshake className="h-4 w-4 text-brand-600 dark:text-primary" />
            Convite de parceria{dados ? ` — ${dados.labName}` : ""}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {todasConectadas
              ? "Você já é parceiro deste laboratório em todas as suas clínicas."
              : multiClinic
                ? "Escolha em quais das suas clínicas conectar a parceria — todas já vêm marcadas."
                : "Este convite conecta uma clínica — escolha qual."}
          </DialogDescription>
        </DialogHeader>

        {consulta.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
          </div>
        ) : consulta.isError ? (
          <p className="py-4 text-sm text-zinc-500 dark:text-muted-foreground">
            {consulta.error.message || "Convite não encontrado ou já finalizado."}
          </p>
        ) : clinicas.length === 0 ? (
          <p className="py-4 text-sm text-zinc-500 dark:text-muted-foreground">
            Você ainda não tem nenhuma organização de clínica — crie uma pra poder conectar a
            parceria. O convite fica guardado e volta a aparecer quando você tiver uma.
          </p>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-y-auto overscroll-contain">
            {clinicas.map((c) => {
              const marcada = selecionadas.has(c.organizationId);
              const desabilitada = c.jaConectada || !c.podeAceitar;
              return (
                <li key={c.organizationId}>
                  <button
                    type="button"
                    disabled={desabilitada}
                    onClick={() => alternar(c.organizationId)}
                    aria-pressed={marcada}
                    className={cn(
                      "flex h-12 w-full items-center gap-3 rounded-md px-3 text-left text-sm transition-colors",
                      desabilitada
                        ? "cursor-default opacity-70"
                        : "text-zinc-800 hover:bg-zinc-50 dark:text-foreground dark:hover:bg-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center border",
                        multiClinic ? "rounded" : "rounded-full",
                        marcada && !desabilitada
                          ? "border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary"
                          : "border-zinc-300 dark:border-border",
                      )}
                    >
                      {marcada && !desabilitada && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <Building2 className="h-4 w-4 shrink-0 text-zinc-400" />
                    <span className="min-w-0 flex-1 truncate">
                      {c.nome}
                      {c.isPersonal && (
                        <span className="ml-1 text-[11px] text-zinc-400 dark:text-muted-foreground">(pessoal)</span>
                      )}
                    </span>
                    {c.jaConectada ? (
                      <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                        Parceria já existe
                      </span>
                    ) : !c.podeAceitar ? (
                      <span
                        title="Só administradores ou o proprietário da clínica podem aceitar parcerias"
                        className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-zinc-500 dark:bg-muted dark:text-muted-foreground"
                      >
                        Só administradores
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={fechar}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            {todasConectadas || semClinicas ? "Fechar" : "Cancelar"}
          </button>
          {semClinicas && (
            <button
              type="button"
              onClick={() => onFechar(true)}
              className="inline-flex h-11 items-center justify-center rounded-md px-3 text-[13px] font-medium text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-700 sm:h-9 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
            >
              Descartar convite
            </button>
          )}
          {todasConectadas && origem?.tipo === "convite" ? (
            <button
              type="button"
              disabled={enviando}
              onClick={() => origem && dispensar.mutate({ id: origem.id })}
              className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-zinc-600 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-zinc-700 disabled:opacity-60 sm:h-9 sm:flex-none"
            >
              {enviando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Dispensar convite
            </button>
          ) : !todasConectadas && clinicas.length > 0 ? (
            <button
              type="button"
              disabled={enviando || selecionadas.size === 0}
              onClick={aceitar}
              className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
            >
              {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Handshake className="h-3.5 w-3.5" />}
              {multiClinic && selecionadas.size > 1 ? `Conectar ${selecionadas.size} clínicas` : "Conectar"}
            </button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
